import React, { useEffect, useRef } from 'react';

const OrangeOrb3D = () => {
  const mountRef = useRef(null);
  const groupRef = useRef(null);
  const dotsRef = useRef([]);
  const draggingRef = useRef(false);
  const prevPosRef = useRef({ x: 0, y: 0 });
  const velocityRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    let cleanup = () => {};

    const init = async () => {
      // Early return if mount point doesn't exist
      if (!mountRef.current) {
        return;
      }

      const THREE = await import('three');

      const scene = new THREE.Scene();
      scene.background = null;

      const width = mountRef.current?.clientWidth || 800;
      const height = mountRef.current?.clientHeight || 400;

      const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
      camera.position.set(0, 0, 6);

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      
      // Disable tone mapping that might be washing out colors
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
      
      // Check again before appendChild
      if (!mountRef.current) {
        return;
      }
      
      // Clear any existing canvases first!
      while (mountRef.current.firstChild) {
        mountRef.current.removeChild(mountRef.current.firstChild);
      }
      
      mountRef.current.appendChild(renderer.domElement);
      renderer.domElement.style.cursor = 'grab';

      // Re-enable lights for particle dots
      const ambient = new THREE.AmbientLight(0xffffff, 0.8);
      scene.add(ambient);
      const dir = new THREE.DirectionalLight(0xffffff, 1.5);
      dir.position.set(-3, 3, 5);
      scene.add(dir);

      // Perfect sphere with corroded texture (20% smaller = 2 * 0.8 = 1.6)
      const geometry = new THREE.SphereGeometry(1.6, 128, 128);
      
      // Create custom shader material for beautiful orange orb
      const material = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          baseColor: { value: new THREE.Color(0xFF2300) },
          emissiveColor: { value: new THREE.Color(0xFF4500) },
          lightPosition: { value: new THREE.Vector3(-3, 3, 5) }
        },
        vertexShader: `
          varying vec3 vNormal;
          varying vec3 vPosition;
          varying vec3 vWorldPosition;
          varying vec2 vUv;
          
          void main() {
            vNormal = normalize(normalMatrix * normal);
            vPosition = position;
            vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform float time;
          uniform vec3 baseColor;
          uniform vec3 emissiveColor;
          uniform vec3 lightPosition;
          varying vec3 vNormal;
          varying vec3 vPosition;
          varying vec3 vWorldPosition;
          varying vec2 vUv;
          
          // Permutation polynomial for pseudo-random
          vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
          vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
          vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
          vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
          
          // Simplex 3D noise
          float snoise(vec3 v) {
            const vec2 C = vec2(1.0/6.0, 1.0/3.0);
            const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
            
            vec3 i  = floor(v + dot(v, C.yyy));
            vec3 x0 = v - i + dot(i, C.xxx);
            
            vec3 g = step(x0.yzx, x0.xyz);
            vec3 l = 1.0 - g;
            vec3 i1 = min(g.xyz, l.zxy);
            vec3 i2 = max(g.xyz, l.zxy);
            
            vec3 x1 = x0 - i1 + C.xxx;
            vec3 x2 = x0 - i2 + C.yyy;
            vec3 x3 = x0 - D.yyy;
            
            i = mod289(i);
            vec4 p = permute(permute(permute(
                     i.z + vec4(0.0, i1.z, i2.z, 1.0))
                   + i.y + vec4(0.0, i1.y, i2.y, 1.0))
                   + i.x + vec4(0.0, i1.x, i2.x, 1.0));
            
            float n_ = 0.142857142857;
            vec3 ns = n_ * D.wyz - D.xzx;
            
            vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
            
            vec4 x_ = floor(j * ns.z);
            vec4 y_ = floor(j - 7.0 * x_);
            
            vec4 x = x_ *ns.x + ns.yyyy;
            vec4 y = y_ *ns.x + ns.yyyy;
            vec4 h = 1.0 - abs(x) - abs(y);
            
            vec4 b0 = vec4(x.xy, y.xy);
            vec4 b1 = vec4(x.zw, y.zw);
            
            vec4 s0 = floor(b0)*2.0 + 1.0;
            vec4 s1 = floor(b1)*2.0 + 1.0;
            vec4 sh = -step(h, vec4(0.0));
            
            vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
            vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
            
            vec3 p0 = vec3(a0.xy, h.x);
            vec3 p1 = vec3(a0.zw, h.y);
            vec3 p2 = vec3(a1.xy, h.z);
            vec3 p3 = vec3(a1.zw, h.w);
            
            vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
            p0 *= norm.x;
            p1 *= norm.y;
            p2 *= norm.z;
            p3 *= norm.w;
            
            vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
            m = m * m;
            return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
          }
          
          // Multi-octave noise for fine grain
          float fbm(vec3 p) {
            float value = 0.0;
            float amplitude = 0.5;
            float frequency = 1.0;
            
            for(int i = 0; i < 5; i++) {
              value += amplitude * snoise(p * frequency);
              frequency *= 2.1;
              amplitude *= 0.48;
            }
            return value * 0.5 + 0.5;
          }
          
          void main() {
            // Beautiful organic color gradients using noise
            float pattern1 = fbm(vPosition * 0.8);
            float pattern2 = fbm(vPosition * 1.3 + vec3(50.0));
            float pattern3 = fbm(vPosition * 0.6 + vec3(100.0));
            
            // Combine patterns
            float colorMix = pattern1 * 0.45 + pattern2 * 0.35 + pattern3 * 0.2;
            
            // Natural orange color range with balanced contrast
            vec3 deepOrange = baseColor * 0.6;
            vec3 richOrange = baseColor * 0.8;
            vec3 baseOrange = baseColor * 1.0;
            vec3 brightOrange = baseColor * 1.25;
            vec3 warmOrange = baseColor * 1.15;
            
            // Smooth gradient through all colors
            vec3 color;
            if (colorMix < 0.25) {
              color = mix(deepOrange, richOrange, colorMix * 4.0);
            } else if (colorMix < 0.5) {
              color = mix(richOrange, baseOrange, (colorMix - 0.25) * 4.0);
            } else if (colorMix < 0.75) {
              color = mix(baseOrange, brightOrange, (colorMix - 0.5) * 4.0);
            } else {
              color = mix(brightOrange, warmOrange, (colorMix - 0.75) * 4.0);
            }
            
            // Fine texture
            float grain = fbm(vPosition * 15.0);
            color *= (0.94 + grain * 0.12);
            
            // Subtle shading - just enough to show dimension
            vec3 lightDir = normalize(vec3(-1.0, 1.0, 1.0));  // Top-left direction
            float lightInfluence = max(dot(vNormal, lightDir), 0.0);
            lightInfluence = smoothstep(0.3, 1.0, lightInfluence);  // Very gentle falloff
            
            // Subtle brightness variation (20% boost on lit side)
            color *= (0.9 + lightInfluence * 0.2);
            
            // Very subtle specular highlight
            vec3 viewDir = normalize(cameraPosition - vWorldPosition);
            vec3 halfDir = normalize(lightDir + viewDir);
            float specular = pow(max(dot(vNormal, halfDir), 0.0), 50.0);
            color += baseColor * specular * 0.15;
            
            // VERY subtle rim glow at edges only
            float rim = 1.0 - max(0.0, dot(vNormal, viewDir));
            rim = pow(rim, 4.0);
            
            vec3 finalColor = color + emissiveColor * rim * 0.3;
            
            gl_FragColor = vec4(finalColor, 1.0);
          }
        `,
      });
      
      const sphere = new THREE.Mesh(geometry, material);
      scene.add(sphere);

      const group = new THREE.Group();
      groupRef.current = group;
      scene.add(group);

      const dotMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xFF2300,
        emissive: 0xFF2300,
        emissiveIntensity: 0.4
      });
      
      dotsRef.current = [];
      // More particles - 120 total (distances reduced by 20%)
      for (let i = 0; i < 120; i++) {
        // Distance from center - more particles further out (scaled to 80%)
        const distRandom = Math.random();
        const r = distRandom < 0.2 ? 1.84 + Math.random() * 0.24 :   // 20% close (2.3*0.8, 0.3*0.8)
                  distRandom < 0.5 ? 2.0 + Math.random() * 0.4 :     // 30% medium (2.5*0.8, 0.5*0.8)
                  2.24 + Math.random() * 0.64;                        // 50% far (2.8*0.8, 0.8*0.8)
        
        // Size inversely proportional to distance - smaller at edges
        const distanceFromCenter = r - 1.84;
        const maxDistance = 1.04;  // 1.3 * 0.8
        const distanceFactor = 1.0 - (distanceFromCenter / maxDistance) * 0.7;
        
        const sizeRandom = Math.random();
        let baseSize = sizeRandom < 0.4 ? 0.008 + Math.random() * 0.012 :  // 40% tiny
                       sizeRandom < 0.7 ? 0.018 + Math.random() * 0.022 :  // 30% small-medium
                       sizeRandom < 0.9 ? 0.035 + Math.random() * 0.03 :   // 20% medium-large
                       0.055 + Math.random() * 0.045;                       // 10% large
        
        // Apply distance factor to make outer dots smaller and whispy
        baseSize *= distanceFactor;
        
        const dotGeometry = new THREE.SphereGeometry(baseSize, 16, 16);
        const dotMat = dotMaterial.clone();
        
        // Reduce emissive intensity for distant whispy dots
        dotMat.emissiveIntensity = 0.4 * distanceFactor;
        dotMat.opacity = 0.6 + distanceFactor * 0.4;
        dotMat.transparent = true;
        
        const dot = new THREE.Mesh(dotGeometry, dotMat);
        
        const phi = Math.acos(2 * Math.random() - 1);
        const theta = 2 * Math.PI * Math.random();
        dot.position.set(
          r * Math.sin(phi) * Math.cos(theta),
          r * Math.cos(phi),
          r * Math.sin(phi) * Math.sin(theta)
        );
        dot.userData.baseScale = 1;
        group.add(dot);
        dotsRef.current.push(dot);
      }

      let animId;
      const animate = () => {
        animId = requestAnimationFrame(animate);

        // Constant rotation - more dynamic movement
        group.rotation.x += velocityRef.current.x;
        group.rotation.y += velocityRef.current.y;
        velocityRef.current.x *= 0.94;
        velocityRef.current.y *= 0.94;

        const base = 0.0015;
        const time = Date.now() * 0.0001;
        sphere.rotation.y += base * (1 + Math.sin(time) * 0.2);
        sphere.rotation.x += base * 0.4 * (1 + Math.cos(time * 0.7) * 0.2);
        group.rotation.y -= base * 0.8;
        group.rotation.x += base * 0.6;

        renderer.render(scene, camera);
      };
      animate();

      const onResize = () => {
        const w = mountRef.current?.clientWidth || width;
        const h = mountRef.current?.clientHeight || height;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener('resize', onResize);

      const getPos = (e) => ('touches' in e ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY });
      const onDown = (e) => {
        draggingRef.current = true;
        renderer.domElement.style.cursor = 'grabbing';
        const p = getPos(e);
        prevPosRef.current = p;
      };
      const onMove = (e) => {
        if (!draggingRef.current) return;
        const p = getPos(e);
        const dx = p.x - prevPosRef.current.x;
        const dy = p.y - prevPosRef.current.y;
        prevPosRef.current = p;
        velocityRef.current.y += dx * 0.0008;
        velocityRef.current.x += dy * 0.0008;
      };
      const onUp = () => {
        draggingRef.current = false;
        renderer.domElement.style.cursor = 'grab';
      };

      renderer.domElement.addEventListener('mousedown', onDown);
      renderer.domElement.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      renderer.domElement.addEventListener('touchstart', onDown, { passive: true });
      renderer.domElement.addEventListener('touchmove', onMove, { passive: true });
      window.addEventListener('touchend', onUp);

      cleanup = () => {
        cancelAnimationFrame(animId);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchend', onUp);
        if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
          mountRef.current.removeChild(renderer.domElement);
        }
        renderer.dispose();
        geometry.dispose();
        material.dispose();
        dotsRef.current.forEach(d => {
          if (d.geometry) d.geometry.dispose();
          if (d.material && d.material.dispose) d.material.dispose();
        });
        dotsRef.current = [];
      };
    };

    const group = groupRef.current;
    init();
    return () => cleanup();
  }, []);

  return (
    <div style={{ width: '100%', height: 360 }} ref={mountRef} />
  );
};

export default OrangeOrb3D; 